import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { currency, fmtDate } from "../../lib/status";

const STATUS_OPTIONS = [
    { value: "", label: "All statuses" },
    { value: "cash_pending", label: "Cash pending (COD)" },
    { value: "paid", label: "Paid" },
    { value: "collected", label: "Collected" },
    { value: "refunded", label: "Refunded" },
    { value: "partial_refund", label: "Partial refund" },
    { value: "failed", label: "Failed" },
];

const METHOD_OPTIONS = [
    { value: "", label: "All methods" },
    { value: "cash", label: "Cash" },
    { value: "wallet", label: "Wallet" },
    { value: "online", label: "Online" },
    { value: "card", label: "Card" },
];

function Modal({ open, onClose, title, children, testid }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            data-testid={testid}
        >
            <div className="bg-[#180A21] border border-[#2B1438] w-full max-w-md">
                <div className="px-6 py-4 border-b border-[#2B1438] flex items-center justify-between">
                    <div className="font-serif text-lg text-white">{title}</div>
                    <button
                        onClick={onClose}
                        className="text-white/60 hover:text-white"
                        data-testid={`${testid}-close`}
                    >
                        ✕
                    </button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
}

export default function AdminPayments() {
    const [txns, setTxns] = useState([]);
    const [refunds, setRefunds] = useState([]);
    const [statusFilter, setStatusFilter] = useState("cash_pending");
    const [methodFilter, setMethodFilter] = useState("");
    const [tab, setTab] = useState("txns"); // txns | refunds

    // Collect modal
    const [collectTarget, setCollectTarget] = useState(null);
    const [collectNote, setCollectNote] = useState("");

    // Refund modal
    const [refundTarget, setRefundTarget] = useState(null);
    const [refundAmount, setRefundAmount] = useState("");
    const [refundReason, setRefundReason] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const params = {};
                if (statusFilter) params.status = statusFilter;
                if (methodFilter) params.method = methodFilter;
                const [t, r] = await Promise.all([
                    api.get("/payments", { params }),
                    api.get("/refunds"),
                ]);
                if (!cancelled) {
                    setTxns(t.data);
                    setRefunds(r.data);
                }
            } catch (e) {
                if (!cancelled) toast.error(formatApiError(e.response?.data?.detail));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [statusFilter, methodFilter]);

    const reload = async () => {
        const params = {};
        if (statusFilter) params.status = statusFilter;
        if (methodFilter) params.method = methodFilter;
        const [t, r] = await Promise.all([
            api.get("/payments", { params }),
            api.get("/refunds"),
        ]);
        setTxns(t.data);
        setRefunds(r.data);
    };

    const collectCOD = async () => {
        try {
            await api.post(`/payments/${collectTarget.id}/collect`, {
                note: collectNote || null,
            });
            toast.success("COD marked collected");
            setCollectTarget(null);
            setCollectNote("");
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const submitRefund = async () => {
        try {
            const payload = {};
            if (refundAmount) payload.amount = parseFloat(refundAmount);
            if (refundReason) payload.reason = refundReason;
            await api.post(
                `/bookings/${refundTarget.booking_id}/refund`,
                payload,
            );
            toast.success("Refund processed → wallet credited");
            setRefundTarget(null);
            setRefundAmount("");
            setRefundReason("");
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const statusBadge = (s) => {
        const map = {
            cash_pending: "bg-yellow-400/20 text-yellow-400 border-yellow-400/40",
            paid: "bg-green-400/20 text-green-400 border-green-400/40",
            collected: "bg-green-400/20 text-green-400 border-green-400/40",
            refunded: "bg-red-400/20 text-red-400 border-red-400/40",
            partial_refund: "bg-orange-400/20 text-orange-400 border-orange-400/40",
            failed: "bg-red-400/20 text-red-400 border-red-400/40",
            pending: "bg-white/10 text-white/60 border-white/20",
        };
        return (
            <span
                className={`text-[10px] px-2 py-0.5 border ${
                    map[s] || "bg-white/10 text-white/60 border-white/20"
                }`}
            >
                {s}
            </span>
        );
    };

    return (
        <div className="space-y-6">
            <div>
                <div className="lx-label text-[#D4AF37] mb-2">Finance</div>
                <h1 className="font-serif text-4xl text-white">Payments & Refunds</h1>
                <p className="text-white/60 text-sm mt-2">
                    Review payment transactions, mark COD collected, and issue refunds to
                    customer wallet.
                </p>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-[#2B1438]">
                {[
                    ["txns", "Transactions"],
                    ["refunds", "Refunds"],
                ].map(([v, l]) => (
                    <button
                        key={v}
                        data-testid={`pay-tab-${v}`}
                        onClick={() => setTab(v)}
                        className={`px-5 py-3 text-sm border-b-2 ${
                            tab === v
                                ? "border-[#D4AF37] text-[#D4AF37]"
                                : "border-transparent text-white/60 hover:text-white"
                        }`}
                    >
                        {l}
                    </button>
                ))}
            </div>

            {tab === "txns" && (
                <>
                    <div className="lx-card-dark p-4 flex flex-wrap gap-3">
                        <select
                            data-testid="pay-status-filter"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-3 py-2 bg-[#0F0614] border border-[#2B1438] text-white text-sm max-w-xs"
                        >
                            {STATUS_OPTIONS.map((s) => (
                                <option key={s.value} value={s.value}>
                                    {s.label}
                                </option>
                            ))}
                        </select>
                        <select
                            data-testid="pay-method-filter"
                            value={methodFilter}
                            onChange={(e) => setMethodFilter(e.target.value)}
                            className="px-3 py-2 bg-[#0F0614] border border-[#2B1438] text-white text-sm max-w-xs"
                        >
                            {METHOD_OPTIONS.map((s) => (
                                <option key={s.value} value={s.value}>
                                    {s.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="lx-card-dark overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-white/60 border-b border-[#2B1438]">
                                <tr>
                                    <th className="p-4 lx-label">Txn</th>
                                    <th className="p-4 lx-label">Booking</th>
                                    <th className="p-4 lx-label">Method</th>
                                    <th className="p-4 lx-label">Status</th>
                                    <th className="p-4 lx-label text-right">Amount</th>
                                    <th className="p-4 lx-label">Date</th>
                                    <th className="p-4 lx-label text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {txns.map((t) => (
                                    <tr
                                        key={t.id}
                                        className="border-b border-[#2B1438]"
                                        data-testid={`txn-row-${t.id}`}
                                    >
                                        <td className="p-4 text-white font-mono text-xs">
                                            {t.id?.slice(0, 8)}
                                        </td>
                                        <td className="p-4 text-white/80 font-mono text-xs">
                                            {t.booking_id?.slice(0, 8) || "—"}
                                        </td>
                                        <td className="p-4 text-white/80">{t.method}</td>
                                        <td className="p-4">{statusBadge(t.status)}</td>
                                        <td className="p-4 text-right font-serif text-[#D4AF37]">
                                            {currency(t.amount, t.currency)}
                                        </td>
                                        <td className="p-4 text-white/60 text-xs">
                                            {fmtDate(t.created_at)}
                                        </td>
                                        <td className="p-4 text-right space-x-2">
                                            {t.method === "cash" &&
                                                (t.status === "cash_pending" ||
                                                    t.status === "pending") && (
                                                    <button
                                                        data-testid={`txn-collect-${t.id}`}
                                                        onClick={() => setCollectTarget(t)}
                                                        className="text-xs text-green-400 hover:underline"
                                                    >
                                                        Mark collected
                                                    </button>
                                                )}
                                            {(t.status === "paid" || t.status === "collected") && (
                                                <button
                                                    data-testid={`txn-refund-${t.id}`}
                                                    onClick={() => {
                                                        setRefundTarget(t);
                                                        setRefundAmount(String(t.amount));
                                                    }}
                                                    className="text-xs text-red-400 hover:underline"
                                                >
                                                    Refund
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {txns.length === 0 && (
                            <div className="p-12 text-center text-white/60">
                                No transactions match the filter.
                            </div>
                        )}
                    </div>
                </>
            )}

            {tab === "refunds" && (
                <div className="lx-card-dark overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-white/60 border-b border-[#2B1438]">
                            <tr>
                                <th className="p-4 lx-label">Refund</th>
                                <th className="p-4 lx-label">Booking</th>
                                <th className="p-4 lx-label">User</th>
                                <th className="p-4 lx-label">Status</th>
                                <th className="p-4 lx-label text-right">Amount</th>
                                <th className="p-4 lx-label">Date</th>
                                <th className="p-4 lx-label">Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            {refunds.map((r) => (
                                <tr
                                    key={r.id}
                                    className="border-b border-[#2B1438]"
                                    data-testid={`refund-row-${r.id}`}
                                >
                                    <td className="p-4 text-white font-mono text-xs">
                                        {r.id?.slice(0, 8)}
                                    </td>
                                    <td className="p-4 text-white/80 font-mono text-xs">
                                        {r.booking_id?.slice(0, 8)}
                                    </td>
                                    <td className="p-4 text-white/80 font-mono text-xs">
                                        {r.user_id?.slice(0, 8)}
                                    </td>
                                    <td className="p-4">{statusBadge(r.status)}</td>
                                    <td className="p-4 text-right font-serif text-[#D4AF37]">
                                        {currency(r.amount, r.currency)}
                                    </td>
                                    <td className="p-4 text-white/60 text-xs">
                                        {fmtDate(r.created_at)}
                                    </td>
                                    <td className="p-4 text-white/60 text-xs">
                                        {r.reason || "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {refunds.length === 0 && (
                        <div className="p-12 text-center text-white/60">No refunds yet.</div>
                    )}
                </div>
            )}

            {/* Collect modal */}
            <Modal
                open={!!collectTarget}
                onClose={() => setCollectTarget(null)}
                title="Mark COD as collected"
                testid="collect-modal"
            >
                <div className="space-y-4">
                    <div className="text-sm text-white/80">
                        Transaction <span className="font-mono">{collectTarget?.id?.slice(0, 8)}</span>{" "}
                        — {currency(collectTarget?.amount, collectTarget?.currency)}
                    </div>
                    <div>
                        <label className="lx-label text-white/60 mb-1.5 block">
                            Note (optional)
                        </label>
                        <input
                            data-testid="collect-note"
                            value={collectNote}
                            onChange={(e) => setCollectNote(e.target.value)}
                            className="w-full px-3 py-2 bg-[#0F0614] border border-[#2B1438] text-white text-sm"
                            placeholder="e.g. Cash received in full"
                        />
                    </div>
                    <p className="text-xs text-white/60">
                        This will also mark the booking as paid + payment_completed and generate
                        an invoice.
                    </p>
                    <button
                        data-testid="collect-confirm"
                        onClick={collectCOD}
                        className="w-full py-2.5 bg-green-500 text-black font-medium hover:bg-green-400"
                    >
                        Confirm collection
                    </button>
                </div>
            </Modal>

            {/* Refund modal */}
            <Modal
                open={!!refundTarget}
                onClose={() => setRefundTarget(null)}
                title="Issue refund"
                testid="refund-modal"
            >
                <div className="space-y-4">
                    <div className="text-sm text-white/80">
                        Transaction <span className="font-mono">{refundTarget?.id?.slice(0, 8)}</span>
                        {" "}— {currency(refundTarget?.amount, refundTarget?.currency)}
                    </div>
                    <div>
                        <label className="lx-label text-white/60 mb-1.5 block">
                            Amount ({refundTarget?.currency || "AED"})
                        </label>
                        <input
                            data-testid="refund-amount"
                            type="number"
                            step="0.01"
                            value={refundAmount}
                            onChange={(e) => setRefundAmount(e.target.value)}
                            className="w-full px-3 py-2 bg-[#0F0614] border border-[#2B1438] text-white text-sm"
                        />
                    </div>
                    <div>
                        <label className="lx-label text-white/60 mb-1.5 block">Reason</label>
                        <textarea
                            data-testid="refund-reason"
                            rows={2}
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                            className="w-full px-3 py-2 bg-[#0F0614] border border-[#2B1438] text-white text-sm"
                        />
                    </div>
                    <p className="text-xs text-white/60">
                        Refund is credited directly to the customer's wallet.
                    </p>
                    <button
                        data-testid="refund-confirm"
                        onClick={submitRefund}
                        className="w-full py-2.5 bg-red-500 text-white font-medium hover:bg-red-400"
                    >
                        Issue refund
                    </button>
                </div>
            </Modal>
        </div>
    );
}
